"use client";

import React from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
	mutationPermissionRequirements,
	type PermissionedMutation,
} from "@/constants/permissions/permissions";
import {
	NoPermissionElement,
	type NoPermissionElementProps,
} from "./NoPermissionElement";
import NoPermissionPage from "./NoPermissionPage";
import {
	ActiveInvestingEntityPermissionsDocument,
	SelectedInvestingEntityPermissionsDocument,
} from "./WithMutationPermission.generated";
import useFeatureFlag from "../utilities/featureFlag/featureFlag";

type WithMutationPermissionProps = {
	mutation: PermissionedMutation;
	children: React.ReactNode;
	fallback?: React.ReactNode;
};

gql`
	query ActiveInvestingEntityPermissions {
		account {
			id
			activeInvestingEntity {
				id
				permissions
			}
		}
	}
`;

export function useActiveInvestingEntityMutationPermission(
	mutation: PermissionedMutation,
) {
	const isPermissionEnabled = useFeatureFlag("INVESTOR_PORTAL_PERMISSIONING");
	const { data, loading } = useQuery(ActiveInvestingEntityPermissionsDocument, {
		skip: !isPermissionEnabled,
	});
	const activePermissions =
		data?.account?.activeInvestingEntity?.permissions ?? [];
	const requiredPermissions = mutationPermissionRequirements[mutation].map(
		({ requires }) => requires,
	);

	if (!isPermissionEnabled) {
		return { hasPermission: true, loading: false };
	}

	if (loading) {
		return { hasPermission: false, loading: true };
	}

	const hasPermission = requiredPermissions.every((requiredPermission) =>
		activePermissions.includes(requiredPermission),
	);

	return { hasPermission, loading: false };
}

gql`
	query SelectedInvestingEntityPermissions {
		account {
			id
			investingEntities {
				id
				permissions
			}
		}
	}
`;

/**
 * Page-local variant of useActiveInvestingEntityMutationPermission: gates a
 * mutation on the *selected* investing entity's permissions rather than the
 * global active IE. Used by the /trade buy-order page.
 * Mirrors the active-IE hook's double-gating: when INVESTOR_PORTAL_PERMISSIONING
 * is off, hasPermission is always true.
 */
export function useSelectedInvestingEntityMutationPermission(
	mutation: PermissionedMutation,
	selectedInvestingEntityId: string | undefined,
) {
	const isPermissionEnabled = useFeatureFlag("INVESTOR_PORTAL_PERMISSIONING");
	const { data, loading } = useQuery(
		SelectedInvestingEntityPermissionsDocument,
		{
			skip: !isPermissionEnabled,
		},
	);

	if (!isPermissionEnabled) {
		return { hasPermission: true, loading: false };
	}

	if (loading) {
		return { hasPermission: false, loading: true };
	}

	const selectedPermissions =
		data?.account?.investingEntities?.find(
			(investingEntity) => investingEntity.id === selectedInvestingEntityId,
		)?.permissions ?? [];
	const requiredPermissions = mutationPermissionRequirements[mutation].map(
		({ requires }) => requires,
	);

	const hasPermission = requiredPermissions.every((requiredPermission) =>
		selectedPermissions.includes(requiredPermission),
	);

	return { hasPermission, loading: false };
}

export function WithMutationPermission({
	mutation,
	children,
	fallback = null,
}: WithMutationPermissionProps) {
	const { hasPermission, loading } =
		useActiveInvestingEntityMutationPermission(mutation);

	if (loading) {
		return fallback;
	}

	if (hasPermission) {
		return children;
	}

	return fallback;
}

type ElementWithMutationPermissionProps = {
	mutation: PermissionedMutation;
} & NoPermissionElementProps &
	Omit<
		React.HTMLAttributes<HTMLElement>,
		keyof NoPermissionElementProps | "children"
	>;

const ElementWithMutationPermission = React.forwardRef<
	HTMLElement,
	ElementWithMutationPermissionProps
>(
	(
		{
			children,
			className,
			noPermissionHint,
			wrapWith,
			mutation,
			...childProps
		},
		ref,
	) => {
		const fallback = (
			<NoPermissionElement
				className={className}
				noPermissionHint={noPermissionHint}
				wrapWith={wrapWith}
			>
				{children}
			</NoPermissionElement>
		);
		const permittedChildren = React.cloneElement(
			children as React.ReactElement<
				React.HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement>
			>,
			{
				...childProps,
				ref,
			},
		);

		return (
			<WithMutationPermission mutation={mutation} fallback={fallback}>
				{permittedChildren}
			</WithMutationPermission>
		);
	},
);

ElementWithMutationPermission.displayName = "ElementWithMutationPermission";

function PageWithMutationPermission({
	children,
	...props
}: Omit<WithMutationPermissionProps, "fallback">) {
	return (
		<WithMutationPermission fallback={<NoPermissionPage />} {...props}>
			{children}
		</WithMutationPermission>
	);
}

WithMutationPermission.Element = ElementWithMutationPermission;
WithMutationPermission.Page = PageWithMutationPermission;
